<script setup lang="ts">
import { useGetProjects } from "@/stores/queries";
import { computed, ref } from "vue";
import { useScreen } from "@/composable/useResponsive";
import { useRoute } from "vue-router";
import ProjectAvatar from "./ProjectAvatar.vue";
import { ProjectStatus, type PaginationParams } from "@/types";

const props = withDefaults(
  defineProps<{
    minimal?: boolean;
  }>(),
  {
    minimal: undefined,
  }
);

const filters = ref({
  status: [ProjectStatus.IN_PROGRESS],
});

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 100,
  sortBy: "createdAt",
  order: "desc",
});

const { data, localData } = useGetProjects(pagination, filters);

const projects = computed(() => {
  return data.value?.projects ?? localData.value ?? [];
});

const route = useRoute();

const activeRoute = computed(() => {
  return route.name ? (route.name as string) : undefined;
});

const { screen } = useScreen();

// Use minimal prop if provided, otherwise fallback to screen-based detection
const isMinimal = computed(() => {
  if (props.minimal !== undefined) {
    return props.minimal;
  }
  return screen.value.lt.lg;
});
</script>

<template>
  <el-menu
    v-if="projects?.length"
    :default-active="activeRoute"
    router
    class="border-0"
  >
    <el-menu-item-group v-if="!isMinimal" class="mt-3 mb-1 px-4">
      <span
        class="text-uppercase fw-light text-center text-dark"
        style="font-size: 0.7rem"
        >{{ $t("projects.in_progress") }}</span
      >
    </el-menu-item-group>
    <el-menu-item-group v-else class="divider"></el-menu-item-group>

    <el-menu-item
      v-for="(project, id) in projects"
      :key="'projectmenu-' + id"
      :index="'projectmenu-' + id"
      :route="{
        name: 'project',
        params: { id: project.id },
      }"
      class="rounded"
      style="height: 3rem"
      :class="isMinimal ? 'mb-2' : 'mb-0'"
    >
      <div
        :class="
          isMinimal
            ? 'w-100 d-flex flex-column gap-1 justify-content-center align-items-center'
            : 'w-100 d-flex gap-2 align-items-center '
        "
      >
        <ProjectAvatar
          :project="project"
          :size="isMinimal ? 40 : 25"
          style="margin: 0"
        />

        <span v-if="!isMinimal" class="text-truncate col">
          {{ project.name }}
        </span>
      </div>
    </el-menu-item>
  </el-menu>
</template>

<style lang="scss" scoped>
.el-menu-item {
  line-height: 1;
}

.el-menu-item.is-active {
  background-color: var(--bs-light);
  color: var(--bs-primary);
}

.divider {
  padding: 0 !important;
  height: 2px;
  width: 100%;
  background-color: var(--bs-light);
  margin-bottom: 1rem;
  margin-top: 1rem;
}
</style>
